import React from "react";
import Switch from "../Switch";
import Dropdown from "../Dropdown";
import InputField from "../InputField";
import MultipleSelectDropdown from "../MultipleSelectDropdown";
import { userOptionLabel } from "./offboardingFormData";

/**
 * What should happen to the offboarded user's mailbox (epic #288): keep it as a shared mailbox
 * for colleagues, forward incoming mail to someone else, and answer senders with an autoreply.
 * The follow-up pickers only appear once the matching switch is on, so the form stays short.
 *
 * @param {object} props
 * @param {object} props.value - The offboarding form value, shaped like EMPTY_OFFBOARDING
 * @param {object[]} props.otherUsers - Colleagues that may take over the mailbox
 * @param {(next: object) => void} props.onChange - Receives a new value; never mutates the old
 */
const MailboxWishesSection = ({ value, otherUsers, onChange }) => {
  const accessOptions = otherUsers.map((user) => ({
    value: user.userPublicId,
    label: userOptionLabel(user),
  }));

  const forwardTarget = otherUsers.find(
    (user) => user.userPublicId === value.forwardToUserPublicId
  );

  const handleForwardSelect = (label) => {
    const user = otherUsers.find((candidate) => userOptionLabel(candidate) === label);
    onChange({ ...value, forwardToUserPublicId: user ? user.userPublicId : "" });
  };

  return (
    <>
      <Switch
        label="Convert to shared mailbox"
        checked={!!value.convertToSharedMailbox}
        onChange={(checked) => onChange({ ...value, convertToSharedMailbox: checked })}
      />

      {value.convertToSharedMailbox && (
        <MultipleSelectDropdown
          label="Who should have access"
          options={accessOptions}
          selected={value.sharedMailboxAccessUserPublicIds || []}
          onChange={(selected) =>
            onChange({ ...value, sharedMailboxAccessUserPublicIds: selected })
          }
          searchable={true}
          placeholder="Choose colleagues"
        />
      )}

      <Switch
        label="Forward mail"
        checked={!!value.forwardMail}
        onChange={(checked) => onChange({ ...value, forwardMail: checked })}
      />

      {value.forwardMail && (
        <Dropdown
          label="Forward to"
          value={forwardTarget ? forwardTarget.displayName || forwardTarget.userPrincipalName : ""}
          options={otherUsers.map(userOptionLabel)}
          onSelect={handleForwardSelect}
          searchable={true}
        />
      )}

      <InputField
        label="Autoreply text"
        value={value.autoReplyText || ""}
        onChange={(event) => onChange({ ...value, autoReplyText: event.target.value })}
        placeholder="What should senders be told after the end date?"
        type="text-area"
      />
    </>
  );
};

export default MailboxWishesSection;
